import { useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageForm from '../../../templates/PageForm';
import RefreshButton from '../../../atoms/RefreshButton';
import StatusReasonModal from '../../communication/StatusReasonModal';
import CollectionPolicyList from './components/CollectionPolicyList';
import useCollectionSettings from './useCollectionSettings';
import collectionApi from '../../../../services/collectionApi';
import { CollectionPolicy } from './collectionModel';
import { reportCollectionError } from './collectionErrors';
import {
    collectionPolicyEditPath,
    collectionPolicyNewPath,
    collectionPoliciesPath,
} from './collectionRoutes';

const CollectionSettingsPage = () => {
    const navigate = useNavigate();
    const { policies, meta, loading, reload } = useCollectionSettings();
    const [deactivating, setDeactivating] = useState<CollectionPolicy | null>(null);

    const reactivate = async (policy: CollectionPolicy) => {
        try {
            await collectionApi.changePolicyStatus(policy.id, 'ACTIVE', null);
            toast.success(`${policy.name} reativada`);
            reload();
        } catch (error) {
            reportCollectionError(error, 'Não foi possível reativar.');
        }
    };

    const togglePolicy = (policy: CollectionPolicy) => (policy.status === 'ACTIVE'
        ? setDeactivating(policy)
        : reactivate(policy));

    const confirmDeactivation = async (reason: string) => {
        if (!deactivating) return;
        const target = deactivating;
        setDeactivating(null);
        try {
            await collectionApi.changePolicyStatus(target.id, 'INACTIVE', reason);
            toast.success(`${target.name} desativada`);
            reload();
        } catch (error) {
            reportCollectionError(error, 'Não foi possível desativar.');
        }
    };

    const breadcrumb = [
        { label: 'Home', url: '/' },
        { label: 'Cobrança', url: '/billing/invoices?status=OPEN&collect=true' },
        { label: 'Políticas de cobrança', url: collectionPoliciesPath() },
    ];
    const controlSlot = (
        <div className="flex items-center justify-end gap-3 m-2">
            <RefreshButton variant="icon" label="lista de políticas" busy={loading} onClick={reload} />
            <button type="button" onClick={() => navigate(collectionPolicyNewPath())}
                className="inline-flex items-center gap-1 text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 whitespace-nowrap">
                <MdAdd size={18} /> Nova política
            </button>
        </div>
    );

    return (
        <PageForm pageTitle="Políticas de cobrança" apiVersion={meta.apiVersion || ''} isLoading={loading} breadcrumb={breadcrumb} controlSlot={controlSlot}>
            <div className="py-4">
                <CollectionPolicyList policies={policies}
                    onEdit={p => navigate(collectionPolicyEditPath(p.id))}
                    onToggleStatus={togglePolicy} />
            </div>

            <StatusReasonModal isOpen={!!deactivating} reasonRequired
                title={deactivating ? `Desativar ${deactivating.name}` : ''} actionLabel="Desativar"
                onCancel={() => setDeactivating(null)} onConfirm={confirmDeactivation} />
        </PageForm>
    );
};

export default CollectionSettingsPage;
